import { useCallback, useEffect, useRef } from 'react'
import { IconSearch } from '@tabler/icons-react'
import clsx from 'clsx'
import { usePathname, useSearchParams } from 'next/navigation'

import Button from 'shared/ui/Button'
import type { ButtonProps } from 'shared/ui/Button/Button'
import { ButtonSizes, ButtonVariants } from 'shared/ui/Button/Button.enums'
import TextField from 'shared/ui/form/TextField'
import { KEYBOARD_KEYS } from 'shared/ui/types'

import styles from './SearchInput.module.scss'

const SearchButton = ({ className, ...props }: ButtonProps) => (
  <Button {...props} className={clsx(styles.searchButton, className)} />
)

const SearchButtonLabel = ({
  $isOpen,
  children
}: {
  $isOpen: boolean
  children?: React.ReactNode
}) => (
  <span
    className={clsx(styles.searchButtonLabel, {
      [styles.searchButtonLabelOpen]: $isOpen
    })}
  >
    {children}
  </span>
)

type SearchInputProps = {
  query: string
  setQuery: (query: string) => void
  isOpen: boolean
  open: () => void
  handleSearchSubmit: (e?: React.FormEvent) => void
}

const SearchInput: React.FC<SearchInputProps> = ({
  query,
  setQuery,
  isOpen,
  open,
  handleSearchSubmit
}) => {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const searchParamsString = searchParams?.toString() ?? ''
  const wrapperRef = useRef<HTMLDivElement>(null)

  // Hijack Enter key to open dropdown. Use only when dropdown is closed.
  const openDropdownOnEnter = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === KEYBOARD_KEYS.ENTER) {
        e.preventDefault()
        e.stopPropagation()
        open()
      }
    },
    [open]
  )

  // Prevent focus keeping when navigating away from search dropdown
  useEffect(() => {
    const activeElement = document.activeElement

    if (
      activeElement instanceof HTMLElement &&
      wrapperRef.current?.contains(activeElement)
    ) {
      activeElement.blur()
    }
  }, [pathname, searchParamsString])

  const searchButton = (
    <SearchButton
      data-open={isOpen}
      size={ButtonSizes.medium}
      aria-label='Wyszukaj'
      variant={ButtonVariants.secondary}
      startIcon={<IconSearch />}
      type='submit'
    >
      <SearchButtonLabel $isOpen={isOpen}>Szukaj</SearchButtonLabel>
    </SearchButton>
  )

  return (
    <div
      className={styles.wrapper}
      ref={wrapperRef}
      onKeyDown={isOpen ? undefined : openDropdownOnEnter}
    >
      <form
        noValidate
        className={styles.inputWrapper}
        role='search'
        onSubmit={handleSearchSubmit}
        autoComplete='off'
      >
        <TextField
          id='search-input'
          placeholder='Wyszukaj produkt'
          marginBottom={false}
          name='searchQuery'
          value={query}
          onClick={open}
          onChange={e => {
            setQuery(e.target.value)
          }}
          icons={{ end: searchButton }}
          inputProps={{
            maxLength: 120,
            role: 'combobox',
            'aria-controls': 'search-results',
            'aria-expanded': isOpen,
            'aria-autocomplete': 'list',
            'aria-haspopup': 'listbox',
            'aria-label': 'Wyszukaj produkt'
          }}
        />
      </form>
    </div>
  )
}

export default SearchInput
